import { useMemo, useEffect, useState, useRef } from 'react';
import { DataGrid, KeenIcon } from '@/components';
import usePagination from '@/hooks/usePagination';
import {
  useGetCommunityUsersQuery,
  useUpdateUserStatusMutation,
  useGetEligibleCommunityUsersQuery,
  useAddCommunityUserMutation,
  useGetCommunityInvitationsQuery,
  useCreateCommunityInvitationMutation,
  useResendCommunityInvitationMutation,
  useCancelCommunityInvitationMutation,
} from '@/services/CommunityUserService';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import {
  CommunityUsersResponse,
  CommunityUser,
  CommunityRole,
  EligibleCommunityUser,
} from '@/types/community-user.types';
import { ConfirmationDialog } from '@/components/dialogBox';
import { enqueueSnackbar } from 'notistack';
import UserRoleModal from '../community-management/components/UserRoleModal';
import { usePermissions } from '@/hooks/usePermissions';
import { formatDisplayDate } from '@/utils/Date';
import { Mail, RefreshCw, UserPlus, X } from 'lucide-react';

interface IUserData {
  id: string;
  name: string;
  email: string;
  joined_at: string;
  blocked: boolean;
  community_role: string;
}

const renderRoleCell = ({ row }: { row: { original: IUserData } }) => {
  const roleColorMap: Record<string, { bg: string; text: string }> = {
    [CommunityRole.READER]: {
      bg: 'bg-blue-100 dark:bg-blue-950 contrast:bg-blue-950',
      text: 'text-blue-800 dark:text-blue-200 contrast:text-blue-200',
    },
    [CommunityRole.CONTRIBUTOR]: {
      bg: 'bg-green-100 dark:bg-green-950 contrast:bg-green-950',
      text: 'text-green-800 dark:text-green-200 contrast:text-green-200',
    },
    [CommunityRole.MODERATOR]: {
      bg: 'bg-purple-100 dark:bg-purple-950 contrast:bg-purple-950',
      text: 'text-purple-800 dark:text-purple-200 contrast:text-purple-200',
    },
    [CommunityRole.COMMUNITY_ADMIN]: {
      bg: 'bg-yellow-100 dark:bg-yellow-950 contrast:bg-yellow-950',
      text: 'text-yellow-800 dark:text-yellow-200 contrast:text-yellow-200',
    },
    default: {
      bg: 'bg-gray-100',
      text: 'text-gray-800',
    },
  };

  const role = row.original.community_role || 'READER';
  const colors = roleColorMap[role] || roleColorMap.default;

  return (
    <span
      className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${colors.bg} ${colors.text}`}
      role="status"
      aria-label={`User role: ${role}`}
    >
      {role}
    </span>
  );
};

const renderJoinedAtCell = ({ row }: { row: { original: IUserData } }) => (
  <span className="text-gray-800 font-normal">
    {formatDisplayDate(new Date(row.original.joined_at))}
  </span>
);

const renderBlockedCell = ({ row }: { row: { original: IUserData } }) => (
  <span
    className={`text-sm font-medium ${
      row.original.blocked ? 'text-red-600' : 'text-green-700 dark:text-green-600 contrast:text-green-600'
    }`}
    role="status"
    aria-label={`User status: ${row.original.blocked ? 'BLOCKED' : 'ACTIVE'}`}
  >
    {row.original.blocked ? 'BLOCKED' : 'ACTIVE'}
  </span>
);

const getApiErrorMessage = (error: unknown, fallback: string) => {
  const data = (
    error as {
      data?: { message?: string };
      response?: { data?: { message?: string } };
    }
  )?.data;
  return (
    data?.message ||
    (error as { response?: { data?: { message?: string } } })?.response?.data
      ?.message ||
    fallback
  );
};

const roleOptions = [
  { value: CommunityRole.READER, label: 'Reader' },
  { value: CommunityRole.CONTRIBUTOR, label: 'Contributor' },
  { value: CommunityRole.MODERATOR, label: 'Moderator' },
  { value: CommunityRole.COMMUNITY_ADMIN, label: 'Community Admin' },
];

const AddCommunityMemberModal = ({
  communityId,
  isOpen,
  onClose,
  onComplete,
}: {
  communityId: string;
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
}) => {
  const [mode, setMode] = useState<'existing' | 'email'>('existing');
  const [search, setSearch] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<CommunityRole>(CommunityRole.READER);
  const [addCommunityUser, { isLoading: isAdding }] =
    useAddCommunityUserMutation();
  const [createInvitation, { isLoading: isInviting }] =
    useCreateCommunityInvitationMutation();

  const { data: eligibleUsers = [], isFetching: isSearching } =
    useGetEligibleCommunityUsersQuery(
      { community_id: communityId, search, limit: 20 },
      { skip: !isOpen || mode !== 'existing' || !communityId }
    );

  useEffect(() => {
    if (!isOpen) return;
    setSearch('');
    setSelectedUserId('');
    setEmail('');
    setRole(CommunityRole.READER);
    setMode('existing');
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    try {
      if (mode === 'existing') {
        if (!selectedUserId) {
          enqueueSnackbar('Select a user to add', { variant: 'warning' });
          return;
        }
        await addCommunityUser({
          community_id: communityId,
          user_id: selectedUserId,
          role,
        }).unwrap();
        enqueueSnackbar('User added to community', { variant: 'success' });
      } else {
        if (!email.trim()) {
          enqueueSnackbar('Enter an email address', { variant: 'warning' });
          return;
        }
        await createInvitation({
          community_id: communityId,
          email: email.trim(),
          role,
        }).unwrap();
        enqueueSnackbar('Invitation created', { variant: 'success' });
      }
      onComplete();
      onClose();
    } catch (error) {
      enqueueSnackbar(
        getApiErrorMessage(
          error,
          mode === 'existing'
            ? 'Failed to add user'
            : 'Failed to create invitation'
        ),
        { variant: 'error' }
      );
    }
  };

  const isBusy = isAdding || isInviting;

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-community-member-title"
    >
      <div className="w-full max-w-lg rounded-lg bg-gray-100 shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
          <h2
            id="add-community-member-title"
            className="text-lg font-semibold text-gray-900"
          >
            Add community member
          </h2>
          <button
            type="button"
            className="btn btn-sm btn-icon btn-light"
            onClick={onClose}
            aria-label="Close"
            disabled={isBusy}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-5 px-5 py-4">
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              className={`btn btn-sm justify-center ${mode === 'existing' ? 'btn-primary' : 'btn-light'}`}
              onClick={() => setMode('existing')}
              disabled={isBusy}
            >
              <UserPlus className="mr-2 h-4 w-4" />
              Existing user
            </button>
            <button
              type="button"
              className={`btn btn-sm justify-center ${mode === 'email' ? 'btn-primary' : 'btn-light'}`}
              onClick={() => setMode('email')}
              disabled={isBusy}
            >
              <Mail className="mr-2 h-4 w-4" />
              Email invite
            </button>
          </div>

          {mode === 'existing' ? (
            <div className="space-y-3">
              <div>
                <label htmlFor="eligible-user-search" className="form-label">
                  Search users
                </label>
                <input
                  id="eligible-user-search"
                  type="text"
                  className="input w-full"
                  placeholder="Name or email"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  disabled={isBusy}
                />
              </div>
              <div>
                <label htmlFor="eligible-user-select" className="form-label">
                  User
                </label>
                <select
                  id="eligible-user-select"
                  className="select w-full"
                  value={selectedUserId}
                  onChange={(e) => setSelectedUserId(e.target.value)}
                  disabled={isBusy || isSearching}
                >
                  <option value="">
                    {isSearching ? 'Searching...' : 'Select user'}
                  </option>
                  {eligibleUsers.map((user: EligibleCommunityUser) => (
                    <option key={user.id} value={user.id}>
                      {user.name || user.email} ({user.email})
                    </option>
                  ))}
                </select>
              </div>
            </div>
          ) : (
            <div>
              <label htmlFor="community-invite-email" className="form-label">
                Email
              </label>
              <input
                id="community-invite-email"
                type="email"
                className="input w-full"
                placeholder="member@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isBusy}
              />
            </div>
          )}

          <div>
            <label htmlFor="community-member-role" className="form-label">
              Role
            </label>
            <select
              id="community-member-role"
              className="select w-full"
              value={role}
              onChange={(e) => setRole(e.target.value as CommunityRole)}
              disabled={isBusy}
            >
              {roleOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex justify-end gap-3 border-t border-gray-200 px-5 py-4">
          <button
            type="button"
            className="btn btn-light"
            onClick={onClose}
            disabled={isBusy}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={isBusy}
          >
            {isBusy
              ? 'Saving...'
              : mode === 'existing'
                ? 'Add user'
                : 'Send invite'}
          </button>
        </div>
      </div>
    </div>
  );
};

const CommunityUsersListings = () => {
  const {
    totalPages,
    totalItems,
    handleTotalPages,
    handleTotalItems,
    debouncedValue,
    handleSearch,
    searchTerm,
    pagination,
    setPagination,
    resetSearch,
  } = usePagination();
  const communityId = useSelector(
    (state: RootState) => state.auth.selectedCommunityId
  );
  const [updateUserStatus, { isLoading: isUpdating }] =
    useUpdateUserStatusMutation();
  const [confirmActionState, setConfirmActionState] = useState<{
    isOpen: boolean;
    userId: string | null;
    action: 'BLOCK' | 'UNBLOCK' | 'DELETE' | null;
  }>({ isOpen: false, userId: null, action: null });
  const [dropdownOpen, setDropdownOpen] = useState<string | null>(null);
  const dropdownMenuRef = useRef<HTMLDivElement | null>(null);
  const [dropdownTriggerRect, setDropdownTriggerRect] =
    useState<DOMRect | null>(null);
  const [dropdownMenuPosition, setDropdownMenuPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [roleModalState, setRoleModalState] = useState<{
    isOpen: boolean;
    user: CommunityUser | null;
  }>({ isOpen: false, user: null });
  const [isAddMemberOpen, setIsAddMemberOpen] = useState(false);
  const { hasPermission } = usePermissions();
  const [resendInvitation, { isLoading: isResendingInvitation }] =
    useResendCommunityInvitationMutation();
  const [cancelInvitation, { isLoading: isCancelingInvitation }] =
    useCancelCommunityInvitationMutation();

  useEffect(() => {
    if (!dropdownOpen || !dropdownTriggerRect) {
      setDropdownMenuPosition(null);
      return;
    }

    const menuEl = dropdownMenuRef.current;
    if (!menuEl) return;

    const menuRect = menuEl.getBoundingClientRect();
    const viewportPadding = 8;
    const verticalOffset = 8;

    const preferredLeft = dropdownTriggerRect.right - menuRect.width;
    const left = Math.min(
      Math.max(viewportPadding, preferredLeft),
      window.innerWidth - menuRect.width - viewportPadding
    );

    const spaceBelow = window.innerHeight - dropdownTriggerRect.bottom;
    const shouldOpenUp = spaceBelow < menuRect.height + verticalOffset;

    const top = shouldOpenUp
      ? Math.max(
          viewportPadding,
          dropdownTriggerRect.top - menuRect.height - verticalOffset
        )
      : Math.min(
          dropdownTriggerRect.bottom + verticalOffset,
          window.innerHeight - menuRect.height - viewportPadding
        );

    setDropdownMenuPosition({ top, left });
  }, [dropdownOpen, dropdownTriggerRect]);

  const queryParams = {
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    search: debouncedValue,
    community_id: communityId || undefined,
  };

  const { data, isLoading, isFetching, isError, refetch } =
    useGetCommunityUsersQuery(queryParams, {
      skip: !communityId,
    }) as {
      data: CommunityUsersResponse;
      isLoading: boolean;
      isFetching: boolean;
      isError: boolean;
      refetch: () => void;
    };

  const {
    data: invitationsData,
    isFetching: isFetchingInvitations,
    refetch: refetchInvitations,
  } = useGetCommunityInvitationsQuery(
    {
      community_id: communityId || '',
      page: 1,
      limit: 10,
      status: 'active',
    },
    { skip: !communityId }
  );

  useEffect(() => {
    if (data) {
      handleTotalPages(data.total_pages || 1);
      handleTotalItems(data.total_items || 0);
    }
  }, [data, handleTotalPages, handleTotalItems]);

  useEffect(() => {
    if (typeof refetch === 'function') {
      refetch();
    }
  }, [debouncedValue, communityId, refetch]);

  const filteredData = useMemo(() => {
    if (!data?.data) return [];
    return data.data.map((user: CommunityUser) => ({
      id: user.id,
      name: user.name || '-',
      email: user.email,
      joined_at: user.joined_at,
      blocked: user.blocked || false,
      community_role: user.community_role || '-',
      user_roles: user.user_roles || [],
    }));
  }, [data]);

  const handleActionClick = (
    userId: string,
    action: 'BLOCK' | 'UNBLOCK' | 'DELETE'
  ) => {
    setConfirmActionState({ isOpen: true, userId, action });
    setDropdownOpen(null);
    setDropdownTriggerRect(null);
  };

  const handleConfirmAction = async () => {
    if (confirmActionState.userId && confirmActionState.action && communityId) {
      try {
        await updateUserStatus({
          community_id: communityId,
          user_id: confirmActionState.userId,
          action: confirmActionState.action,
        }).unwrap();
        refetch();
        enqueueSnackbar(
          `User ${confirmActionState.action.toLowerCase()}ed successfully`,
          { variant: 'success' }
        );
        setConfirmActionState({
          isOpen: false,
          userId: null,
          action: null,
        });
      } catch (error) {
        console.error('Failed to block user:', error);
        enqueueSnackbar(
          (error as { response?: { data?: { message?: string } } })?.response
            ?.data?.message || 'Failed to update user status',
          { variant: 'error' }
        );
      }
    }
  };

  const handleResendInvitation = async (invitationId: string) => {
    if (!communityId) return;
    try {
      await resendInvitation({
        community_id: communityId,
        invitation_id: invitationId,
      }).unwrap();
      enqueueSnackbar('Invitation resent', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(
        getApiErrorMessage(error, 'Failed to resend invitation'),
        {
          variant: 'error',
        }
      );
    }
  };

  const handleCancelInvitation = async (invitationId: string) => {
    if (!communityId) return;
    try {
      await cancelInvitation({
        community_id: communityId,
        invitation_id: invitationId,
      }).unwrap();
      enqueueSnackbar('Invitation cancelled', { variant: 'success' });
      if (typeof refetchInvitations === 'function') {
        refetchInvitations();
      }
    } catch (error) {
      enqueueSnackbar(
        getApiErrorMessage(error, 'Failed to cancel invitation'),
        {
          variant: 'error',
        }
      );
    }
  };

  const renderActionsCell = ({ row }: { row: { original: IUserData } }) => {
    return (
      <div className="relative">
        <button
          type="button"
          onClick={(e) => {
            const isOpening = dropdownOpen !== row.original.id;
            setDropdownOpen(isOpening ? row.original.id : null);
            setDropdownTriggerRect(
              isOpening ? e.currentTarget.getBoundingClientRect() : null
            );
          }}
          className="btn btn-sm btn-icon btn-light btn-outline focus:outline-none focus:ring-2 focus:ring-blue-500"
          title="Actions"
          aria-label={`Actions for ${row.original.name}`}
          aria-haspopup="true"
          aria-expanded={dropdownOpen === row.original.id}
        >
          <KeenIcon
            type="filled"
            icon="dots-vertical"
            className="text-gray-500"
            aria-hidden="true"
          />
        </button>
        {dropdownOpen === row.original.id && (
          <div
            className="fixed inset-0 z-[9999]"
            onClick={() => {
              setDropdownOpen(null);
              setDropdownTriggerRect(null);
            }}
          >
            <div
              ref={dropdownMenuRef}
              className="fixed z-[10000] w-56 origin-top-right rounded-md bg-gray-100 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
              style={
                dropdownMenuPosition
                  ? {
                      top: dropdownMenuPosition.top,
                      left: dropdownMenuPosition.left,
                    }
                  : undefined
              }
              onClick={(e) => e.stopPropagation()}
              role="menu"
              aria-label="User actions"
            >
              <div className="py-1">
                {hasPermission('community_management_update') && (
                  <>
                    <button
                      type="button"
                      onClick={() =>
                        handleActionClick(
                          row.original.id,
                          row.original.blocked ? 'UNBLOCK' : 'BLOCK'
                        )
                      }
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      role="menuitem"
                      aria-label={`${row.original.blocked ? 'Unblock' : 'Block'} ${row.original.name}`}
                      disabled={isUpdating || isLoading || isFetching}
                    >
                      {row.original.blocked ? 'Unblock' : 'Block'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setRoleModalState({
                          isOpen: true,
                          user: row.original as unknown as CommunityUser,
                        });
                        setDropdownOpen(null);
                        setDropdownTriggerRect(null);
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-blue-600 hover:bg-gray-100"
                      role="menuitem"
                      aria-label={`Change role for ${row.original.name}`}
                      disabled={isUpdating || isLoading || isFetching}
                    >
                      Change Role
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        handleActionClick(row.original.id, 'DELETE')
                      }
                      className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                      role="menuitem"
                      aria-label={`Remove ${row.original.name}`}
                      disabled={isUpdating || isLoading || isFetching}
                    >
                      Remove
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const columns = useMemo(
    () => [
      {
        accessor: (row: IUserData) => row.name,
        id: 'name',
        header: 'Name',
        enableSorting: false,
        cell: ({ row }: { row: { original: IUserData } }) => (
          <span className="text-sm font-medium text-gray-900">
            {row.original.name}
          </span>
        ),
        meta: { className: 'min-w-[200px]' },
      },
      {
        accessor: (row: IUserData) => row.email,
        id: 'email',
        header: 'Email',
        enableSorting: false,
        cell: ({ row }: { row: { original: IUserData } }) => (
          <span className="text-gray-800 font-normal">
            {row.original.email}
          </span>
        ),
        meta: { className: 'min-w-[200px]' },
      },
      {
        accessor: (row: IUserData) => row.community_role,
        id: 'community_role',
        header: 'Community Role',
        enableSorting: false,
        cell: renderRoleCell,
        meta: { className: 'min-w-[200px]' },
      },
      {
        accessor: (row: IUserData) => row.joined_at,
        id: 'joined_at',
        header: 'Joined At',
        enableSorting: false,
        cell: renderJoinedAtCell,
        meta: { className: 'min-w-[150px]' },
      },
      {
        accessor: (row: IUserData) => row.blocked,
        id: 'blocked',
        header: 'Blocked',
        enableSorting: false,
        cell: renderBlockedCell,
        meta: { className: 'min-w-[100px]' },
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: renderActionsCell,
        meta: { className: 'w-[80px]' },
      },
    ],
    [
      isLoading,
      isFetching,
      isUpdating,
      dropdownOpen,
      handleActionClick,
      setDropdownOpen,
      setRoleModalState,
    ]
  );

  return (
    <div className="card card-grid min-w-full">
      <div className="card-header flex flex-wrap items-center gap-2 sm:gap-4 lg:gap-6 justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex-shrink-0" role="search">
            <label
              className="input input-sm h-8 sm:h-10 flex items-center"
              htmlFor="search-users"
              aria-label="Search users"
            >
              <KeenIcon
                type="filled"
                icon="magnifier"
                className="text-gray-500"
                aria-hidden="true"
              />
              <input
                id="search-users"
                placeholder="Search users"
                type="text"
                value={searchTerm}
                onChange={handleSearch}
                className="w-full sm:w-48 h-8 sm:h-10 text-sm input border-0 scale-y-[0.9] rounded-none"
                aria-label="Search users by name or email"
                role="searchbox"
                disabled={isLoading || isFetching || isUpdating}
              />
            </label>
          </div>
          {searchTerm && (
            <button
              type="button"
              className="btn btn-sm btn-light flex items-center gap-2 h-8 sm:h-10"
              onClick={() => {
                resetSearch();
                setPagination((prev) => ({ ...prev, pageIndex: 0 }));
              }}
              aria-label="Clear search"
              title="Clear search"
              disabled={isLoading || isFetching || isUpdating}
            >
              <KeenIcon
                type="solid"
                icon="arrows-circle"
                className="text-gray-600"
                aria-hidden="true"
              />
              Clear
            </button>
          )}
        </div>
        {hasPermission('community_management_update') && (
          <button
            type="button"
            className="btn btn-sm btn-light border border-gray-300 flex items-center gap-2 h-8 sm:h-10"
            onClick={() => setIsAddMemberOpen(true)}
            disabled={!communityId || isLoading || isFetching || isUpdating}
          >
            <UserPlus className="h-4 w-4" />
            Add or invite
          </button>
        )}
      </div>
      <div className="card-body">
        {hasPermission('community_management_read') &&
        invitationsData?.data?.length ? (
          <div className="mb-5 rounded-lg border border-gray-200 bg-gray-50 p-4">
            <div className="mb-3 flex items-center justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">
                  Pending invitations
                </h3>
                <p className="text-xs text-gray-500">
                  {invitationsData.total_items} active invitation
                  {invitationsData.total_items === 1 ? '' : 's'}
                </p>
              </div>
              {isFetchingInvitations && (
                <KeenIcon
                  type="filled"
                  icon="spinner"
                  className="animate-spin text-gray-500"
                />
              )}
            </div>
            <div className="divide-y divide-gray-200">
              {invitationsData.data.map((invitation) => (
                <div
                  key={invitation.id}
                  className="flex flex-col gap-3 py-3 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-gray-900">
                      {invitation.email}
                    </div>
                    <div className="text-xs text-gray-500">
                      {invitation.role} · expires{' '}
                      {formatDisplayDate(new Date(invitation.expires_at))}
                    </div>
                  </div>
                  {hasPermission('community_management_update') && (
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        className="btn btn-xs btn-light flex items-center gap-1"
                        onClick={() => handleResendInvitation(invitation.id)}
                        disabled={
                          isResendingInvitation || isCancelingInvitation
                        }
                      >
                        <RefreshCw className="h-3.5 w-3.5" />
                        Resend
                      </button>
                      <button
                        type="button"
                        className="btn btn-xs btn-light text-red-600"
                        onClick={() => handleCancelInvitation(invitation.id)}
                        disabled={
                          isResendingInvitation || isCancelingInvitation
                        }
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ) : null}
        {isLoading || isFetching || isUpdating ? (
          <div
            className="text-center py-4"
            role="status"
            aria-live="polite"
            aria-busy="true"
          >
            <KeenIcon
              type="filled"
              icon="spinner"
              className="text-gray-500 text-2xl animate-spin"
              aria-hidden="true"
            />
            <span className="ml-2 text-gray-700">Loading...</span>
          </div>
        ) : isError ? (
          <div
            className="text-center py-4 text-red-600"
            role="alert"
            aria-live="assertive"
          >
            Error loading users. Please try again.
          </div>
        ) : (
          <>
            {!isLoading && !isError && filteredData.length === 0 && (
              <div className="text-center py-4">
                <p className="text-gray-500">No users found</p>
              </div>
            )}
            {!isLoading && !isError && filteredData.length > 0 && (
              <DataGrid
                data-testid="users-data-grid"
                columns={columns}
                data={filteredData}
                rowSelect={false}
                loading={isLoading || isFetching || isUpdating}
                pagination={{
                  page: pagination.pageIndex,
                  size: pagination.pageSize,
                  totalItems: data?.total_items || totalItems,
                  totalPages: data?.total_pages || totalPages,
                  setPagination,
                  info: `{from} - {to} of {count}`,
                  sizes: [5, 10, 25, 50, 100],
                  sizesLabel: 'Show',
                  sizesDescription: 'per page',
                }}
                sorting={[{ id: 'name', desc: false }]}
                serverSide={true}
                hasActiveFilters={!!searchTerm}
                aria-label="Community users table"
              />
            )}
          </>
        )}
        <ConfirmationDialog
          open={confirmActionState.isOpen}
          onClose={() =>
            setConfirmActionState({
              isOpen: false,
              userId: null,
              action: null,
            })
          }
          onConfirm={handleConfirmAction}
          message={`Are you sure you want to ${confirmActionState.action === 'BLOCK' ? 'block' : confirmActionState.action === 'UNBLOCK' ? 'unblock' : 'remove'} this user?`}
          cancelButtonText="No"
          confirmButtonText="Yes"
          confirmButtonVariant={
            confirmActionState.action === 'DELETE' ? 'warning' : 'default'
          }
          aria-labelledby="action-confirmation-title"
          aria-describedby="action-confirmation-description"
        />
        {communityId && (
          <UserRoleModal
            isOpen={roleModalState.isOpen}
            user={roleModalState.user}
            communityId={communityId}
            onClose={() => setRoleModalState({ isOpen: false, user: null })}
            onRoleUpdated={() => {
              if (typeof refetch === 'function') {
                refetch();
              }
            }}
          />
        )}
        {communityId && (
          <AddCommunityMemberModal
            communityId={communityId}
            isOpen={isAddMemberOpen}
            onClose={() => setIsAddMemberOpen(false)}
            onComplete={() => {
              if (typeof refetch === 'function') {
                refetch();
              }
              if (typeof refetchInvitations === 'function') {
                refetchInvitations();
              }
            }}
          />
        )}
      </div>
    </div>
  );
};

export default CommunityUsersListings;
